import { attachPublishedVendor, type FetchPublishedVendor } from './attachPublishedVendor'
import {
  IEnum_Solution_Type,
  type IProductBySlugQuery,
  type IProductBySlugQueryVariables,
} from './generated/requests'

type BaseProductBySlug = (
  variables: IProductBySlugQueryVariables,
  requestHeaders?: any,
) => Promise<IProductBySlugQuery>

function normalizeSlug(value: string | undefined): string {
  return value?.toLowerCase().trim() ?? ''
}

/**
 * Calls the generated productBySlug request, then narrows to Product-type solutions
 * and picks one by slug (case-insensitive) or by comma-separated oldSlug entries.
 * Replaces nested draft vendor with the published vendor for the same documentId.
 */
export async function productBySlugCustom(
  baseProductBySlug: BaseProductBySlug,
  variables: IProductBySlugQueryVariables,
  requestHeaders?: any,
  fetchPublishedVendor?: FetchPublishedVendor,
): Promise<IProductBySlugQuery> {
  const response = await baseProductBySlug(variables, requestHeaders)
  const solutions =
    response.solutions?.filter((s) => s?.type === IEnum_Solution_Type.Product) ?? []

  const targetSlug = normalizeSlug(variables.slug)
  if (!targetSlug) {
    return { solutions: [] }
  }

  const exactMatch = solutions.find((s) => normalizeSlug(s?.slug ?? undefined) === targetSlug)
  const oldSlugMatch = solutions.find((s) => {
    const oldSlug = s?.oldSlug ?? ''
    const parts = oldSlug.split(',').map((part) => normalizeSlug(part))
    return parts.includes(targetSlug)
  })
  const match = exactMatch ?? oldSlugMatch
  if (!match) {
    return { solutions: [] }
  }

  if (!fetchPublishedVendor) {
    return { solutions: [match] }
  }

  const withPublishedVendor = await attachPublishedVendor(match, fetchPublishedVendor, requestHeaders)
  return { solutions: [withPublishedVendor] }
}
